import React, { useEffect, useState } from 'react';
import { Card, Grid, Box, Skeleton, Typography } from '@mui/material';
import SummaryCommonCard from 'src/components/reports/summary-cards/summaryCommonCard';
import DonutChart from 'src/components/reports/summary-cards/summaryDonutCard';
import ReportHttpServices from 'src/services/reportsHttpService';
import { internalReportModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useLocation } from 'react-router-dom';
import NoReportCard from 'src/components/reports/summary-cards/noReportCard';
import MapChart from '../charts/map-chart';
import ColumnChart from '../charts/column.charts';
import BarChart from '../charts/bar-chart';
import TableDonutChart from '../summary-cards/summaryTableDonutCard';
import LineChart from '../charts/line-chart';
import StackedBarChart from '../charts/stacked-bar-chart';
import MultiplePostCard from '../charts/multiple-post-card';

const Charts = ({ connectedProfileId, startDate, endDate, viewBy, audienceSummary, serviceName }) => {
  const [isLoading, setLoading] = useState(true);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlBrandId = urlParams.get('brand_id');
  const urlToken = urlParams.get('token');

  const [summaryData, setSummaryData] = useState([]);
  const { showToast } = useToaster();

  useEffect(() => {
    if (connectedProfileId) {
      getAudienceSummary();
    }
  }, [connectedProfileId, startDate, endDate]);

  const getAudienceSummary = () => {
    setLoading(true);
    let body = {
      connected_profile_id: connectedProfileId,
      report_id: audienceSummary.id,
      date_range: {
        from: startDate,
        to: endDate,
      },
      view_by: 'day',
    };
    let getProps = {
      url: `${internalReportModuleURL.scheduledReports}${serviceName}`,
      body: body,
      brandId: urlBrandId,
      token: urlToken,
      successCallback,
      failureCallback,
    };
    ReportHttpServices.Post(getProps);
  };

  const failureCallback = (message) => {
    setLoading(false);
    showToast(message, 'e');
    setSummaryData([]);
  };

  const successCallback = (data) => {
    const responseData = Array.isArray(data[0]?.data) ? data[0]?.data : [];
    setSummaryData(responseData);
    setLoading(false);
  };
  const renderComponent = (data) => {
    const commonStyle = {
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        transform: 'translateY(-2px)',
      },
    };

    switch (data.card_type) {
      case 'map-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <MapChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'column-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <ColumnChart title={data?.title} series={data?.series} categories={data?.categories} />
            </Card>
          </Grid>
        );
      case 'bar-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <BarChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'stacked-bar-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <StackedBarChart title={data?.title} series={data?.series} categories={data?.categories} />
            </Card>
          </Grid>
        );
      case 'line-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <LineChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'multiple-post-card':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <MultiplePostCard posts={data?.posts} />
            </Card>
          </Grid>
        );
      case 'table_with_donut':
        return (
          <Grid item xs={12} lg={6} key={data.id}>
            <Card sx={commonStyle}>
              <TableDonutChart
                title={data?.title}
                engagementPostType={data?.engagement_by_type}
                value={data?.value}
                labels={data?.key}
              />
            </Card>
          </Grid>
        );
      case 'no-report':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <NoReportCard />
            </Card>
          </Grid>
        );
      default:
        return null;
    }
  };

  const renderLoadingSkeleton = () => (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} lg={2.4} key={index}>
          <Box
            sx={{
              height: '200px',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
            }}
          >
            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" sx={{ borderRadius: '12px' }} />
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  const renderEmptyState = () => (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} lg={2.4} key={index}>
          <Box
            sx={{
              height: '100%',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <NoReportCard />
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Card
      sx={{
        position: 'relative',
        p: 3,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        border: '1px solid #e2e8f0',
      }}
    >
      {isLoading ? (
        renderLoadingSkeleton()
      ) : (
        <>
          {summaryData?.length > 0 ? (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="stretch">
                {summaryData?.map((data) => renderComponent(data))}
              </Grid>
            </Box>
          ) : (
            renderEmptyState()
          )}
        </>
      )}
    </Card>
  );
};

export default Charts;
